import { type AxiosInstance } from 'axios'

interface ActivityRow {
  id?: number
  name: string
  categories: string[]
}
interface EmissionFactor {
  sector: string
  unit: string
  source: string
}
type PollutantsPayload = {
  name: string
  chemical_formula: string
  ar: string
  gwp: number

}
type SectionData = {
  order: number
  title: string
  description: string
}
type updateDetailPayload = {
  activity_id: string
  source: number
  region: string
  sector: string
  emission_unit: string
  unit: number
  pollutant: string
  method: string
  factor: string
  description: string
  year: number
  calculation_method: string
}
type ActivityPayload = {
  activity_desc: string
  activity_id: string
  activity_name: string
  canViewActionButtons: boolean
  categories: number[]
  common_identifier: string
  created_by_super_typewriter: boolean
}
type RegionData = {
  region_code: string
  region_name: string
}

type QuestionData = {
  title: string
  body: object | string
  info_guide: string
  section: number | string
  status: string
  theme: string
  published_at: string
  index: string
}
type TemplateData = {
  name: string
  framework: number | string
  version_year: number | string
  type_of_reporting: string
  other_type: string
  template: object | string
  status: string
}

type RftIds = {
  rft_ids: Array<string>
}

type ReportingFramework = {
  rf_id: string
  name: string
  description?: string
  logo?: string
  faq_url?: string
  framework_url?: string
  tutorial_video?: string
  short_form: string
  created_on?: string
  updated_on?: string
}
type UpdatedSourcePayload = {
  canViewActionButtons?: boolean
  common_identifier?: string
  created_by_super_typewriter?: boolean
  source_code: string
  source_id: number
  source_name: string
  source_url?: string
}
type SourcePayload = {
  source_code: string
  source_name: string
  source_url: string
}
type MeasurementPayload = {
  name: string
  unit: string
  unit_description: string
}
type UnitPayload = {
  conversion_factor: number
  description: string
  measurement_common_identifier?: string
  name: string
}
type Units = {
  common_identifiers: string[]
}

type UploadPayload = ActivityRow[]
type EmissionFactorPayload = EmissionFactor[]

export default (axios: AxiosInstance) => ({

  deleteRegions(code: number | string) {
    return axios.delete(`/v1/backstage/efregions/${code}/`)
  },
  updateRegions(code: number | string, data: RegionData) {
    return axios.put(`/v1/backstage/efregions/${code}/`, data)
  },
  createRegion(data: RegionData) {
    return axios.post(`/v1/backstage/efregions/?subdomains=all`, data)
  },

  updateQuestions(qnId: number | string, data: QuestionData) {
    return axios.put(`/v1/backstage/questions/${qnId}/`, data)
  },

  createQuestion(data: QuestionData) {
    return axios.post('/v1/backstage/questions/?subdomains=all', data)
  },
  createFramework(formData: ReportingFramework) {
    return axios.post('v1/backstage/reporting-frameworks/?subdomains=all', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      } })
  },
  updateFramework(rfId: number | string, data: ReportingFramework) {
    return axios.patch(`/v1/backstage/reporting-frameworks/${rfId}/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      } })
  },
  deleteTemplates(data: RftIds) {
    return axios.delete(`/v1/backstage/reporting-frameworks/templates/delete/multiple-delete/`, { data })
  },

  createTemplate(data: TemplateData) {
    return axios.post(`/v1/backstage/reporting-frameworks/create/templates/?subdomains=all`, data)
  },

  updateDraftTemplate(rfId: number | string, data: TemplateData) {
    return axios.put(`/v1/backstage/reporting-frameworks/templates/update-draft/${rfId}/`, data)
  },
  updateTemplate(rfId: number | string, data: TemplateData) {
    return axios.put(`/v1/backstage/reporting-frameworks/templates/${rfId}/`, data)
  },

  deleteEfActivity(id: number) {
    return axios.delete(`/v1/backstage/efactivities/${id}/`)
  },
  getApiEndpoint(is_super_typewriter: boolean, activity_id: string, isEditing: boolean): string {
    return is_super_typewriter
      ? isEditing
        ? `/v1/backstage/efactivities/${activity_id}/`
        : `/v1/backstage/efactivities/?subdomains=all`
      : isEditing
        ? `/v1/efdb/activities/${activity_id}/`
        : `/v1/efdb/activities/`
  },

  updateActivity(is_super_typewriter: boolean, activity_id: string, payload: ActivityPayload) {
    const endpoint = this.getApiEndpoint(is_super_typewriter, activity_id, true)
    return axios.put(endpoint, payload)
  },

  createActivity(is_super_typewriter: boolean, payload: ActivityPayload) {
    const endpoint = this.getApiEndpoint(is_super_typewriter, '', false)
    return axios.post(endpoint, payload, {
    })
  },
  overrideActivity(payload: object, is_super_typewriter: boolean) {
    const endpoint = is_super_typewriter
      ? `/v1/backstage/efactivities-override/`
      : `/v1/efdb/activities-override/`

    return axios.post(endpoint, payload)
  },
  uploadBulkData(payload: UploadPayload) {
    return axios.post('/v1/backstage/efactivities-multiple-create/?subdomains=all', payload)
  },
  overrideUloadedActivity(data: UploadPayload) {
    return axios.post(`/v1/backstage/efactivities-bulk-upload-override/`, data)
  },
  bulkEmissionFactor(importedData: EmissionFactorPayload) {
    return axios.post(`/v1/backstage/emission-factors/bulk/bulk-create/?subdomains=all`, importedData)
  },
  deleteEmissionFactor(id: number | string) {
    return axios.delete(`/v1/backstage/emission-factors/${id}/`)
  },
  updateEmissionFactor(efid: number | string, updatedDetails: updateDetailPayload) {
    return axios.put(`/v1/backstage/emission-factors/${efid}/`, updatedDetails)
  },
  deleteBackstagePollutants(shortcode: string) {
    return axios.delete(`/v1/backstage/pollutants/${shortcode}/`)
  },
  savePollutants(data: PollutantsPayload) {
    return axios.post(`/v1/backstage/pollutants/?subdomains=all`, data)
  },
  updateBackstagePollutants(shortcode: string, data: PollutantsPayload) {
    return axios.put(`/v1/backstage/pollutants/${shortcode}/`, data)
  },
  deleteBackstageMetric(metricId: number | string) {
    return axios.delete(`/v1/backstage/questions/metrics/${metricId}/`)
  },
  updateSections(sectId: string, sectionData: SectionData) {
    return axios.put(`/v1/backstage/questions/sections/${sectId}/`, sectionData)
  },
  deleteEfsources(sourceCode: string) {
    return axios.delete(`/v1/backstage/efsources/${sourceCode}/`)
  },
  updateEfsources(selectedSourceCode: string, data: UpdatedSourcePayload) {
    return axios.put(`/v1/backstage/efsources/${selectedSourceCode}/`, data)
  },
  createEfsources(sourceData: SourcePayload) {
    return axios.post(`/v1/backstage/efsources/?subdomains=all`, sourceData)
  },
  sourcesOverride(sourceData: UpdatedSourcePayload) {
    return axios.post(`/v1/backstage/efsources-override/`, sourceData)
  },
  createMeasurements(measurementsData: MeasurementPayload) {
    return axios.post(`/v1/backstage/measurements/?subdomains=all`, measurementsData)
  },
  createUnits(unitData: UnitPayload) {
    return axios.post(`/v1/backstage/units/?subdomains=all`, unitData)
  },
  updateUnitMeasurement(query: string | number, commonIdentifier: string | number, unitData: UnitPayload) {
    return axios.put(`/v1/backstage/units/measurement/${query}/${commonIdentifier}/`, unitData)
  },
  updateMeasurement(query: string, data: MeasurementPayload) {
    return axios.put(`/v1/backstage/measurements/${query}/`, data)
  },
  deleteMultipleUnits(commonId: number | string, data: Units) {
    return axios.delete(`/v1/backstage/units/delete/multiple-delete/${commonId}/`, { data })
  },
  deleteMeasurement(commonId: number | string) {
    return axios.delete(`/v1/backstage/measurements/${commonId}/`)
  },
  getGhgTemplates() {
    return axios.get<BackstageGhgTemplate[]>('/v1/backstage/ghg-templates/')
  },
  uploadGhgTemplate(formData: FormData) {
    return axios.post('/v1/backstage/ghg-templates/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  getGhgCategories(tenantSchema: string) {
    return axios.get<BackstageGhgCategory[]>(`/v1/backstage/ghg-templates/categories/${tenantSchema}/`)
  },
  getEsgTemplates() {
    return axios.get<BackstageEsgTemplate[]>('/v1/backstage/esg-templates/')
  },
  uploadEsgTemplate(formData: FormData) {
    return axios.post('/v1/backstage/esg-templates/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  getEsgDataCollectionTemplates(tenantSchema: string) {
    return axios.get<BackstageEsgDataCollectionTemplate[]>(`/v1/backstage/esg-templates/data-collection-templates/${tenantSchema}/`)
  },
})
